import { Phone, Mail, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";

interface QuickContactProps {
  title?: string;
  description?: string;
}

const QuickContact = ({ 
  title,
  description
}: QuickContactProps) => {
  const { language } = useLanguage();
  
  const defaultTitles = {
    en: "Need Immediate Legal Help?",
    es: "¿Necesita Ayuda Legal Inmediata?",
    vi: "Cần Trợ Giúp Pháp Lý Ngay?"
  };
  
  const defaultDescriptions = {
    en: "Contact us now for a consultation. We're ready to help with your case.",
    es: "Contáctenos ahora para una consulta. Estamos listos para ayudarle con su caso.",
    vi: "Liên hệ với chúng tôi ngay để được tư vấn. Chúng tôi sẵn sàng giúp đỡ vụ việc của bạn."
  };
  
  const requestConsultation = {
    en: "Request Consultation",
    es: "Solicitar Consulta",
    vi: "Yêu Cầu Tư Vấn"
  };

  return (
    <div className="bg-gradient-gold-shine p-8 rounded-xl shadow-gold">
      <div className="max-w-4xl mx-auto text-center">
        <h3 className="text-3xl font-bold mb-3 text-primary-dark">
          {title || defaultTitles[language]}
        </h3>
        <p className="text-lg mb-6 text-primary-dark/90">
          {description || defaultDescriptions[language]}
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* English Phone */}
          <a href="tel:8329303059" className="block">
            <div className="bg-white/90 backdrop-blur-sm p-6 rounded-lg hover:bg-white hover:shadow-xl transition-all duration-300 group">
              <Phone className="h-8 w-8 text-primary mx-auto mb-3 group-hover:scale-110 transition-transform" />
              <p className="text-sm font-semibold text-primary mb-1">ENGLISH</p>
              <p className="text-lg font-bold text-primary">(832) 930-3059</p>
            </div>
          </a>

          {/* Spanish Phone */}
          <a href="tel:8323567254" className="block">
            <div className="bg-white/90 backdrop-blur-sm p-6 rounded-lg hover:bg-white hover:shadow-xl transition-all duration-300 group">
              <Phone className="h-8 w-8 text-primary mx-auto mb-3 group-hover:scale-110 transition-transform" />
              <p className="text-sm font-semibold text-primary mb-1">ESPAÑOL</p>
              <p className="text-lg font-bold text-primary">(832) 356-7254</p>
            </div>
          </a>

          {/* Vietnamese Phone */}
          <a href="tel:8329876543" className="block">
            <div className="bg-white/90 backdrop-blur-sm p-6 rounded-lg hover:bg-white hover:shadow-xl transition-all duration-300 group">
              <Phone className="h-8 w-8 text-primary mx-auto mb-3 group-hover:scale-110 transition-transform" />
              <p className="text-sm font-semibold text-primary mb-1">TIẾNG VIỆT</p>
              <p className="text-lg font-bold text-primary">(832) 987-6543</p>
            </div>
          </a>

          {/* Email */}
          <a href="mailto:info@danielalbertlaw.com" className="block">
            <div className="bg-white/90 backdrop-blur-sm p-6 rounded-lg hover:bg-white hover:shadow-xl transition-all duration-300 group">
              <Mail className="h-8 w-8 text-primary mx-auto mb-3 group-hover:scale-110 transition-transform" />
              <p className="text-sm font-semibold text-primary mb-1">EMAIL</p>
              <p className="text-sm font-bold text-primary break-all">info@danielalbertlaw.com</p>
            </div>
          </a>
        </div>

        <div className="mt-6 pt-6 border-t border-primary-dark/20">
          <Button asChild size="lg" variant="default" className="bg-primary hover:bg-primary-dark shadow-lg">
            <LocalizedLink to="/appointment">
              <MessageSquare className="mr-2 h-5 w-5" />
              {requestConsultation[language]}
            </LocalizedLink>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickContact;
